import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, FileText, Ruler, Plus, Calendar, Stethoscope } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDataStale } from "@/contexts/DataStaleContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";

interface MedicalReportRecord {
  type: "medical_report";
  structure_type?: "report" | "metrics";
  date: string;
  is_main_record: boolean;
  name?: string | null;
  // Campos para relatório
  report_type?: string | null;
  classification?: string | null;
  technique?: string | null;
  observations?: string | null;
  conclusions?: string | null;
  // Campos para métricas
  metric_name?: string;
  metric_name_slug?: string;
  value?: number;
  unit?: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileMedicalReportDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableMedicalReportRecord extends MedicalReportRecord {
  _index?: number;
}

export function SmartfileMedicalReportDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileMedicalReportDetailsProps) {
  const isMobile = useIsMobile();
  const { markBothStale } = useDataStale();

  // Buscar o registro principal do laudo (is_main_record: true, structure_type: "report" ou sem structure_type)
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'medical_report' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (!(record as any).structure_type || (record as any).structure_type === 'report')
    ) as MedicalReportRecord | undefined;
  }, [session.structured_records]);

  // Buscar o registro principal de métricas (is_main_record: true, structure_type: "metrics")
  const mainMetricsRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'medical_report' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'metrics'
    ) as MedicalReportRecord | undefined;
  }, [session.structured_records]);

  // Separar registros por structure_type (excluir registros principais)
  const allRecords = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is MedicalReportRecord =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'medical_report' &&
        !(record as any).is_main_record
      );
  }, [session.structured_records]);

  // Filtrar registros de relatório (structure_type: "report" ou sem structure_type)
  const reportRecords: EditableMedicalReportRecord[] = useMemo(() => {
    return allRecords
      .filter((record: MedicalReportRecord) =>
        !record.structure_type || record.structure_type === 'report'
      )
      .map((record: MedicalReportRecord, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Filtrar métricas (structure_type: "metrics")
  const metricsRecords: EditableMedicalReportRecord[] = useMemo(() => {
    return allRecords
      .filter((record: MedicalReportRecord) =>
        record.structure_type === 'metrics'
      )
      .map((record: MedicalReportRecord, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableMedicalReportRecord[]>(reportRecords);
  const [editedMainRecord, setEditedMainRecord] = useState<MedicalReportRecord | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando reportRecords mudar
  React.useEffect(() => {
    setEditedRecords(reportRecords);
    setEditedMainRecord(mainRecord);
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let reportCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'medical_report' &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'report')) {
          if (reportCount === index) {
            realIndex = i;
            break;
          }
          reportCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let reportCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'medical_report' &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'report')) {
          if (reportCount === index) {
            realIndex = i;
            break;
          }
          reportCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(reportRecords);
    setEditingIndex(null);
  };

  const handleSaveMainRecord = async () => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'medical_report' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (!(record as any).structure_type || (record as any).structure_type === 'report')
      );

      if (mainRecordIndex !== -1 && editedMainRecord) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Laudo médico atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelMainRecord = () => {
    setEditedMainRecord(mainRecord);
    setEditingMainRecord(false);
  };

  // Estados para métricas
  const [editingMetricsIndex, setEditingMetricsIndex] = useState<number | null>(null);
  const [editingMainMetricsRecord, setEditingMainMetricsRecord] = useState(false);
  const [editedMetricsRecords, setEditedMetricsRecords] = useState<EditableMedicalReportRecord[]>(metricsRecords);
  const [editedMainMetricsRecord, setEditedMainMetricsRecord] = useState<MedicalReportRecord | undefined>(mainMetricsRecord);
  const [isSavingMetrics, setIsSavingMetrics] = useState(false);
  const [dateSortOrderMetrics, setDateSortOrderMetrics] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsMetrics, setExpandedDateGroupsMetrics] = useState<Map<string, boolean>>(new Map());

  React.useEffect(() => {
    setEditedMetricsRecords(metricsRecords);
    setEditedMainMetricsRecord(mainMetricsRecord);
  }, [session.structured_records]);

  const handleSaveMetrics = async (index: number) => {
    setIsSavingMetrics(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let metricsCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'medical_report' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'metrics') {
          if (metricsCount === index) {
            realIndex = i;
            break;
          }
          metricsCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedMetricsRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Métrica atualizada com sucesso!");
        setEditingMetricsIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingMetrics(false);
    }
  };

  const handleDeleteMetrics = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let metricsCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'medical_report' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'metrics') {
          if (metricsCount === index) {
            realIndex = i;
            break;
          }
          metricsCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Métrica excluída com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelMetrics = () => {
    setEditedMetricsRecords(metricsRecords);
    setEditingMetricsIndex(null);
  };

  const handleSaveMainMetricsRecord = async () => {
    setIsSavingMetrics(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainMetricsRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'medical_report' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'metrics'
      );

      if (mainMetricsRecordIndex !== -1 && editedMainMetricsRecord) {
        updatedRecords[mainMetricsRecordIndex] = editedMainMetricsRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de métricas atualizado com sucesso!");
        setEditingMainMetricsRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingMetrics(false);
    }
  };

  const handleCancelMainMetricsRecord = () => {
    setEditedMainMetricsRecord(mainMetricsRecord);
    setEditingMainMetricsRecord(false);
  };

  // Função para adicionar nova métrica
  const handleAddMetric = async () => {
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const date = mainRecord?.date || new Date().toISOString().split('T')[0];

      const newMetric: MedicalReportRecord = {
        type: 'medical_report',
        structure_type: 'metrics',
        date: date,
        is_main_record: false,
        metric_name: '',
        metric_name_slug: '',
        value: undefined,
        unit: ''
      };

      updatedRecords.push(newMetric);

      const { error } = await supabase
        .from('smartfile_sessions')
        .update({ structured_records: updatedRecords as any })
        .eq('id', session.id);

      if (error) throw error;

      if (onRecordsChange) {
        onRecordsChange(updatedRecords);
      }

      // Marcar visão 360 e análise global como desatualizados
      if (session.patient_id) {
        await markBothStale(session.patient_id);
      }

      toast.success("✅ Nova métrica adicionada!");
      onRefresh();
    } catch (error: any) {
      console.error('Erro ao adicionar métrica:', error);
      toast.error("❌ Erro ao adicionar métrica");
    }
  };

  if (!mainRecord && reportRecords.length === 0 && metricsRecords.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum laudo médico encontrado nesta sessão</p>
      </div>
    );
  }

  // Agrupar métricas por mês/ano
  const groupedByDateMetrics = metricsRecords.reduce((acc, record, index) => {
    const date = record.date || session.created_at;
    const dateObj = new Date(date);
    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableMedicalReportRecord[] }>);

  const sortedDatesMetrics = Object.keys(groupedByDateMetrics).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderMetrics === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const toggleDateSortMetrics = () => {
    setDateSortOrderMetrics((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleDateGroupMetrics = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsMetrics((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Classificações comuns para laudos médicos
  const classificationOptions = [
    "Baixo Risco",
    "Risco Moderado",
    "Alto Risco",
    "Normal",
    "Alterado",
    "Inconclusivo",
    "Negativo",
    "Positivo"
  ];

  // Função para renderizar linha de métrica
  const renderMetricsRow = (record: EditableMedicalReportRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingMetricsIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-metric-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedMetricsRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedMetricsRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedMetricsRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nome da Métrica</Label>
                    <Input
                      value={editedMetricsRecords[originalIndex]?.metric_name || ''}
                      onChange={(e) => {
                        const updated = [...editedMetricsRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          metric_name: e.target.value || undefined
                        };
                        setEditedMetricsRecords(updated);
                      }}
                      placeholder="Ex: Espessura artéria"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedMetricsRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedMetricsRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value ? parseFloat(e.target.value) : undefined
                        };
                        setEditedMetricsRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedMetricsRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedMetricsRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || undefined
                        };
                        setEditedMetricsRecords(updated);
                      }}
                      placeholder="cm, mm, mL"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveMetrics(originalIndex)} disabled={isSavingMetrics}>
                    {isSavingMetrics ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelMetrics} disabled={isSavingMetrics}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{format(new Date(record.date), "dd/MM/yyyy")}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate">
                {record.metric_name || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.value !== undefined && record.value !== null ? record.value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingMetricsIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta métrica?
                        <br />
                        <span className="font-medium mt-2 block">
                          {format(new Date(record.date), "dd/MM/yyyy")} - {record.metric_name || 'Métrica'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteMetrics(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile de métrica
  const renderMobileMetricsCard = (record: EditableMedicalReportRecord, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingMetricsIndex === originalIndex;

    return (
      <Card key={`${dateKey}-metric-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedMetricsRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedMetricsRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedMetricsRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Nome da Métrica</Label>
                <Input
                  value={editedMetricsRecords[originalIndex]?.metric_name || ''}
                  onChange={(e) => {
                    const updated = [...editedMetricsRecords];
                    updated[originalIndex] = { ...updated[originalIndex], metric_name: e.target.value || undefined };
                    setEditedMetricsRecords(updated);
                  }}
                  placeholder="Ex: Espessura artéria"
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedMetricsRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedMetricsRecords];
                      updated[originalIndex] = { ...updated[originalIndex], value: e.target.value ? parseFloat(e.target.value) : undefined };
                      setEditedMetricsRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={editedMetricsRecords[originalIndex]?.unit || ''}
                    onChange={(e) => {
                      const updated = [...editedMetricsRecords];
                      updated[originalIndex] = { ...updated[originalIndex], unit: e.target.value || undefined };
                      setEditedMetricsRecords(updated);
                    }}
                    placeholder="cm, mm, mL"
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveMetrics(originalIndex)} disabled={isSavingMetrics} className="flex-1">
                  {isSavingMetrics ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelMetrics} disabled={isSavingMetrics} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: Título, Data e Botões de Ação */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Stethoscope className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-semibold text-sm leading-tight">
                      {record.metric_name || 'Métrica não especificada'}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(record.date), "dd/MM/yyyy")}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingMetricsIndex(originalIndex)}
                    className="h-8 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Excluir
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta métrica?
                          <br />
                          <span className="font-medium mt-2 block">
                            {format(new Date(record.date), "dd/MM/yyyy")} - {record.metric_name || 'Métrica'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteMetrics(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Duas colunas: Valor, Unidade */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Valor</span>
                  <span className="text-sm font-medium">{record.value ?? '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Unidade</span>
                  <span className="text-sm font-medium">{record.unit || '–'}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="bg-background border-t">
      {/* Seção: Registro Principal do Laudo */}
      {mainRecord && (
        <div className="mb-4 border-b border-border pb-4">
          <div className="px-4 py-2 bg-primary/10 border-b border-primary/20">
            <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Laudo Médico - {mainRecord.date ? format(new Date(mainRecord.date), "dd/MM/yyyy") : 'Data não informada'}
            </h3>
          </div>
          <div className="p-4">
            {editingMainRecord ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Tipo de Exame</Label>
                    <Input
                      value={editedMainRecord?.report_type || ''}
                      onChange={(e) => {
                        setEditedMainRecord({
                          ...editedMainRecord!,
                          report_type: e.target.value || null
                        });
                      }}
                      placeholder="Ex: Tomografia artérias coronárias"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Classificação</Label>
                    <select
                      value={editedMainRecord?.classification || ''}
                      onChange={(e) => {
                        setEditedMainRecord({
                          ...editedMainRecord!,
                          classification: e.target.value || null
                        });
                      }}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Selecione...</option>
                      {classificationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Técnica</Label>
                  <Textarea
                    value={editedMainRecord?.technique || ''}
                    onChange={(e) => {
                      setEditedMainRecord({
                        ...editedMainRecord!,
                        technique: e.target.value || null
                      });
                    }}
                    className="min-h-[60px]"
                    placeholder="Descrição da técnica utilizada..."
                  />
                </div>
                <div>
                  <Label className="text-xs">Observações</Label>
                  <Textarea
                    value={editedMainRecord?.observations || ''}
                    onChange={(e) => {
                      setEditedMainRecord({
                        ...editedMainRecord!,
                        observations: e.target.value || null
                      });
                    }}
                    className="min-h-[80px]"
                    placeholder="Observações do exame..."
                  />
                </div>
                <div>
                  <Label className="text-xs">Conclusões</Label>
                  <Textarea
                    value={editedMainRecord?.conclusions || ''}
                    onChange={(e) => {
                      setEditedMainRecord({
                        ...editedMainRecord!,
                        conclusions: e.target.value || null
                      });
                    }}
                    className="min-h-[60px]"
                    placeholder="Conclusões do laudo..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveMainRecord} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelMainRecord} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tipo e Classificação */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Tipo</Label>
                      <p className="text-sm font-medium mt-1">
                        {mainRecord.report_type || 'Não informado'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Classificação</Label>
                      <div className="mt-1">
                        <Badge variant="outline" className="text-sm">
                          {mainRecord.classification || 'Não informada'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingMainRecord(true)}
                    className="h-7 px-2"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                </div>

                {/* Técnica */}
                {mainRecord.technique && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Técnica</Label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {mainRecord.technique}
                    </p>
                  </div>
                )}

                {/* Observações */}
                {mainRecord.observations && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Observações</Label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {mainRecord.observations}
                    </p>
                  </div>
                )}

                {/* Conclusões */}
                {mainRecord.conclusions && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Conclusões</Label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {mainRecord.conclusions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Seção: Métricas */}
      <div>

        {metricsRecords.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <p className="text-sm">Nenhuma métrica adicionada ainda</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddMetric}
              className="mt-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              Adicionar Métrica
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile: Cards View */}
            {isMobile ? (
              <div className="p-3">
                {sortedDatesMetrics.map((monthYearKey) => {
                  const dateGroup = groupedByDateMetrics[monthYearKey];
                  const dateRecords = dateGroup.records;
                  const displayKey = dateGroup.displayKey;
                  const defaultExpanded = expandAllByDefault;
                  const manualState = expandedDateGroupsMetrics.get(monthYearKey);
                  const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                  return (
                    <div key={monthYearKey} className="mb-4">
                      <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupMetrics(monthYearKey, open)}>
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-2 text-xs font-semibold text-green-600 cursor-pointer bg-green-500/5 hover:bg-green-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            <Ruler className="h-4 w-4" />
                            <span>📅 {displayKey}</span>
                            <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                            <Badge variant="secondary">
                              {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {dateRecords.map((record, groupIndex) => renderMobileMetricsCard(record, groupIndex, monthYearKey))}
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Desktop: Table View */
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '200px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '120px' }} />
                  </colgroup>

                  <tbody>
                    {sortedDatesMetrics.map((monthYearKey) => {
                      const dateGroup = groupedByDateMetrics[monthYearKey];
                      const dateRecords = dateGroup.records;
                      const displayKey = dateGroup.displayKey;
                      const defaultExpanded = expandAllByDefault;
                      const manualState = expandedDateGroupsMetrics.get(monthYearKey);
                      const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                      return (
                        <React.Fragment key={monthYearKey}>
                          <tr className="bg-green-500/5 border-y-2 border-green-500/20">
                            <td colSpan={5} className="p-2">
                              <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupMetrics(monthYearKey, open)}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-green-600 cursor-pointer hover:bg-green-500/10 rounded px-2 py-1 transition-colors w-full">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <Ruler className="h-4 w-4" />
                                    <span>📅 {displayKey}</span>
                                    <span className="ml-auto font-normal text-muted-foreground mr-2">
                                      {isExpanded ? '- Ver menos' : '+ Ver mais'}
                                    </span>
                                    <Badge variant="secondary">
                                      {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </CollapsibleTrigger>
                              </Collapsible>
                            </td>
                          </tr>

                          {isExpanded && (
                            <>
                              {dateRecords.map((record, groupIndex) => renderMetricsRow(record, groupIndex, monthYearKey))}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
              Total: {metricsRecords.length} métrica(s) em {sortedDatesMetrics.length} mês(es)/ano(s)
            </div>
          </>
        )}
      </div>
    </div>
  );
}

